<template>
  <v-container fluid class="invoices-top">
    <!-- KPIs Dashboard - Visible en ambas vistas -->
    <v-card class="mb-4" flat elevation="0">
      <v-card-title class="pa-0">
        <v-toolbar flat color="transparent">
          <v-toolbar-title class="text-h6 font-weight-medium">
            <v-icon left small class="mr-2">mdi-chart-box-outline</v-icon>
            Resumen
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- NUEVO: POS -->
          <v-btn text :to="{ name: posRouteName }">
            <v-icon left small>mdi-cash-register</v-icon>
            POS
          </v-btn>
          <!-- Botón existente -->
          <v-btn text to="InvoiceE">Nuevo</v-btn>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-row>
          <!-- KPI: Total de facturas -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="transition-swing pa-3">
              <div class="text-overline grey--text mb-1">Total documentos</div>
              <div class="text-h4 font-weight-light mb-1">
                {{ credit_notes.length }}
              </div>
              <div class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-file-document-outline</v-icon>
                Documentos del día
              </div>
            </v-card>
          </v-col>

          <!-- KPI: Monto total -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="transition-swing pa-3">
              <div class="text-overline grey--text mb-1">Monto total</div>
              <div class="text-h4 font-weight-light mb-1 success--text">
                {{ formatCurrency(totalAmount) }}
              </div>
              <div class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-currency-eur</v-icon>
                Valor acumulado
              </div>
            </v-card>
          </v-col>

          <!-- KPI: Promedio por factura -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="transition-swing pa-3">
              <div class="text-overline grey--text mb-1">Promedio</div>
              <div class="text-h4 font-weight-light mb-1 info--text">
                {{ formatCurrency(averageAmount) }}
              </div>
              <div class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-calculator</v-icon>
                Por documento
              </div>
            </v-card>
          </v-col>

          <!-- KPI: Distribución por tipo -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="transition-swing pa-3">
              <div class="text-overline grey--text mb-1">Facturas / NC</div>
              <div class="text-h4 font-weight-light mb-1">
                <span class="primary--text">{{ documentsByType.FC || 0 }}</span>
                <span class="grey--text text--lighten-1 mx-1">/</span>
                <span class="warning--text">{{ documentsByType.NC || 0 }}</span>
              </div>
              <div class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-file-chart-outline</v-icon>
                Distribución
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Vista de escritorio para pantallas md y mayores -->
    <v-card class="d-none d-md-block" flat>
      <v-toolbar flat color="transparent">
        <v-toolbar-title class="text-h6 font-weight-medium">
          <v-icon left small class="mr-2">mdi-file-document-outline</v-icon>
          Facturas
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- NUEVO: Toggle desktop -->
        <v-btn
          small
          outlined
          class="mr-2"
          :to="{ name: $route.name === 'InvoicesBoard' ? 'Invoices' : 'InvoicesBoard' }"
        >
          <v-icon left small>
            {{ $route.name === "InvoicesBoard" ? "mdi-table" : "mdi-view-dashboard" }}
          </v-icon>
          {{ $route.name === "InvoicesBoard" ? "Tabla" : "Tablero" }}
        </v-btn>
        <!-- NUEVO: POS desktop -->
        <v-btn small outlined color="primary" class="mr-2" :to="{ name: posRouteName }">
          <v-icon left small>mdi-cash-register</v-icon>
          POS
        </v-btn>
        <v-text-field
          v-model="issueDate"
          prepend-icon="mdi-calendar"
          hide-details
          solo
          flat
          dense
          type="date"
          @input="get_notes"
          :disabled="isCashier"
          style="max-width: 220px"
          class="mt-0 pt-0"
        />
      </v-toolbar>

      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="credit_notes"
        :items-per-page="10"
        item-key="id"
        sort-by="issuedate"
        class="elevation-0"
        :loading="loading_status"
        loader-height="2"
        loading-text="Cargando ..."
        @click:row="get_e_doc"
        :class="{ 'row-pointer': !loading_status }"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20],
          'items-per-page-text': 'Facturas por página',
        }"
      >
        <template v-slot:[`item.issuedate`]="{ item }">
          <span class="text-body-2">{{ formatDate(item.issuedate) }}</span>
        </template>

        <template v-slot:[`item.doc_number`]="{ item }">
          <span class="font-weight-medium">{{ item.doc_number }}</span>
        </template>

        <template v-slot:[`item.doc_value`]="{ item }">
          <span class="font-weight-medium">{{ formatCurrency(item.doc_value) }}</span>
        </template>

        <template v-slot:[`item.cufe`]="{ item }">
          <v-icon small :color="item.cufe ? 'success' : 'grey lighten-3'">
            {{ item.cufe ? "mdi-check-circle" : "mdi-circle-outline" }}
          </v-icon>
        </template>

        <template v-slot:[`item.cufe_n`]="{ item }">
          <v-chip
            x-small
            outlined
            :color="item.cufe_n ? 'info' : 'grey'"
            class="font-weight-medium"
            v-if="item.cufe_n"
          >
            NC
          </v-chip>
          <v-icon v-else small color="grey lighten-3"> mdi-circle-outline </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Vista móvil solo para xs y sm -->
    <v-card class="d-md-none" flat>
      <v-toolbar flat color="transparent">
        <v-toolbar-title class="text-h6 font-weight-medium">
          <v-icon left small class="mr-2">mdi-file-document-outline</v-icon>
          Facturas
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- NUEVO: Toggle móvil -->
        <v-btn
          icon
          :to="{ name: $route.name === 'InvoicesBoard' ? 'Invoices' : 'InvoicesBoard' }"
        >
          <v-icon small>{{
            $route.name === "InvoicesBoard" ? "mdi-table" : "mdi-view-dashboard"
          }}</v-icon>
        </v-btn>
        <!-- NUEVO: POS móvil -->
        <v-btn icon :to="{ name: posRouteName }">
          <v-icon small>mdi-cash-register</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-3">
        <v-text-field
          v-model="issueDate"
          prepend-icon="mdi-calendar"
          hide-details
          solo
          flat
          dense
          type="date"
          @input="get_notes"
          :disabled="isCashier"
          class="mb-3"
        />
      </v-card-text>

      <v-divider></v-divider>

      <v-list two-line class="py-0" v-if="!loading_status && credit_notes.length > 0">
        <template v-for="(item, index) in credit_notes">
          <v-hover v-slot="{ hover }" :key="item.id">
            <v-list-item
              @click="get_e_doc(item)"
              :class="{ 'grey lighten-5': hover }"
              class="px-4"
            >
              <v-list-item-avatar size="40" color="grey lighten-4">
                <span class="grey--text text--darken-1 text-caption font-weight-medium">
                  {{ item.doc_number.substring(0, 2) }}
                </span>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium text-body-2">
                  {{ item.doc_number }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  <span>{{ formatDate(item.issuedate) }}</span>
                  <span class="mx-1">•</span>
                  <span>{{ item.invoice }}</span>
                </v-list-item-subtitle>

                <div class="d-flex align-center mt-2">
                  <v-icon
                    x-small
                    :color="item.cufe ? 'success' : 'grey lighten-2'"
                    class="mr-2"
                  >
                    {{ item.cufe ? "mdi-check-circle" : "mdi-circle-outline" }}
                  </v-icon>
                  <v-chip x-small outlined color="info" v-if="item.cufe_n"> NC </v-chip>
                </div>
              </v-list-item-content>

              <v-list-item-action>
                <span class="font-weight-medium text-body-2">
                  {{ formatCurrency(item.doc_value) }}
                </span>
              </v-list-item-action>
            </v-list-item>
          </v-hover>
          <v-divider
            v-if="index < credit_notes.length - 1"
            :key="'div-' + index"
          ></v-divider>
        </template>
      </v-list>

      <!-- Estados de la lista: cargando y vacío -->
      <div v-if="loading_status" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="grey"
          size="40"
          width="2"
        ></v-progress-circular>
        <div class="mt-3 text-body-2 grey--text">Cargando facturas...</div>
      </div>

      <div v-if="!loading_status && credit_notes.length === 0" class="text-center py-8">
        <v-icon color="grey lighten-2" size="48"> mdi-file-document-outline </v-icon>
        <div class="grey--text text-body-2 mt-3">
          No hay facturas disponibles para esta fecha
        </div>
        <v-btn
          text
          small
          color="primary"
          class="mt-3"
          @click="
            issueDate = getToday();
            get_notes();
          "
        >
          <v-icon left small>mdi-refresh</v-icon>
          Volver a hoy
        </v-btn>
      </div>
    </v-card>

    <noteViewer
      :item="item"
      :dialog="dialog"
      @close="dialog = false"
      @refresh="refresh()"
    />
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency, parseLocalDate } from "@/services/webserver.js";
import { isCashier } from "@/utils/roles";
import noteViewer from "@/components/doc_viewer.vue";

export default {
  components: { noteViewer },
  data() {
    return {
      errors: [],
      valid: false,
      loading_status: false,
      f_required: [(v) => !!v || "Requerido"],
      f_email: [
        (v) => !!v || "Correo valido",
        (v) => /.+@.+/.test(v) || "Correo no valido",
      ],
      imageUrl: null,
      dialog: false,
      headers: [
        {
          text: "Fecha",
          align: "start",
          sortable: true,
          value: "issuedate",
          dataType: "text",
          width: "120px",
        },
        {
          text: "Número de Documento",
          align: "start",
          sortable: true,
          value: "doc_number",
          dataType: "text",
        },
        {
          text: "Documento",
          align: "start",
          sortable: true,
          value: "doc_type",
          dataType: "text",
        },
        {
          text: "ID tercero",
          align: "start",
          sortable: true,
          value: "typeid",
          dataType: "text",
        },
        {
          text: "No. Identificación",
          align: "start",
          sortable: true,
          value: "PartyIdentification",
          dataType: "text",
        },

        {
          text: "Nombre",
          align: "start",
          sortable: true,
          value: "PartyName",
          dataType: "text",
        },

        {
          text: "Serie",
          align: "start",
          sortable: true,
          value: "invoice",
          dataType: "text",
        },
        {
          text: "Valor",
          align: "end",
          sortable: true,
          value: "doc_value",
          dataType: "number",
          width: "130px",
        },
        {
          text: "CUFE",
          align: "center",
          sortable: true,
          value: "cufe",
          dataType: "number",
          width: "80px",
        },
        {
          text: "NC",
          align: "center",
          sortable: true,
          value: "cufe_n",
          dataType: "number",
          width: "80px",
        },
      ],
      credit_notes: [],
      item: { document: null, items: [], payments: [] },
      issueDate: getToday(),
    };
  },
  computed: {
    // Cajero: solo ve las facturas del día, sin poder cambiar la fecha
    isCashier() {
      return isCashier(this.$store.getters.profile);
    },
    posRouteName() {
      const mode = this.$store.state.billingMode || "register";
      const map = {
        register: "Home",
        formal: "InvoiceE",
        pos: "InvoiceQ",
        restaurant: "InvoicePOS",
      };
      return map[mode] || "Home";
    },
    // Cálculo del monto total de todos los documentos
    totalAmount() {
      if (!this.credit_notes || this.credit_notes.length === 0) return 0;
      return this.credit_notes.reduce((total, doc) => {
        // Para NC (notas de crédito), considerar como negativo el valor
        const value =
          doc.doc_type === "NC"
            ? -parseFloat(doc.doc_value || 0)
            : parseFloat(doc.doc_value || 0);
        return total + value;
      }, 0);
    },
    // Cálculo del promedio por documento
    averageAmount() {
      if (!this.credit_notes || this.credit_notes.length === 0) return 0;
      return this.totalAmount / this.credit_notes.length;
    },
    // Conteo de documentos por tipo
    documentsByType() {
      if (!this.credit_notes || this.credit_notes.length === 0)
        return { FC: 0, NC: 0, ND: 0, TK: 0, FS: 0 };

      return this.credit_notes.reduce(
        (counts, doc) => {
          const type = doc.doc_type || "OTRO";
          counts[type] = (counts[type] || 0) + 1;
          return counts;
        },
        { FC: 0, NC: 0, ND: 0, TK: 0, FS: 0 }
      );
    },
  },
  mounted() {
    this.get_notes();
  },
  methods: {
    formatCurrency,

    formatDate(dateString) {
      if (!dateString) return "";
      // parseLocalDate evita el desfase de un día por interpretar la fecha como UTC
      const date = parseLocalDate(dateString);
      if (!date) return "";
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    refresh() {
      this.get_notes();
    },
    get_notes() {
      if (this.isCashier) {
        this.issueDate = getToday();
      }
      this.loading_status = true;
      var qry = {
        store: window.store.store_id,
        table: "docs",
      };
      qry.filters = [
        {
          field: "doc_type",
          operator: " IN ",
          value: "'TK','FS','FC','FE','NC','ND'",
        },
        {
          field: "DATE(issuedate)",
          operator: "=",
          value: this.issueDate,
        },
        {
          field: "store",
          operator: "=",
          value: window.store.store_id,
        },
      ];
      if (this.$store.getters.profile.user_access == "USUARIO") {
        qry.filters.push({
          field: "userId",
          operator: "=",
          value: this.$store.state.profile.email,
        });
      }
      console.log(qry);
      webserver("get_dataTable", qry, (data) => {
        console.log(data);
        this.loading_status = false;
        this.credit_notes = data;
      });
    },
    get_e_doc(e) {
      var issudate = e.issuedate.split(" ");
      console.log(issudate[0]);
      var doc = {
        store: e.store,
        issueDate: issudate[0],
        did: e.doc_number,
      };
      console.log(doc);
      webserver("get_invoice_fe", doc, (data) => {
        console.log(data);
        this.item = data;
        this.dialog = true;
        this.loading_vendors = false;
      });
    },
    getToday() {
      return getToday();
    },
  },
};
</script>

<style scoped>
/* Transiciones suaves */
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Hover en filas de tabla */
.row-pointer >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
  cursor: pointer;
}

/* Eliminar bordes innecesarios en la tabla */
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td {
  border-bottom: thin solid rgba(0, 0, 0, 0.06) !important;
}

/* Estilo minimalista para headers de tabla */
.v-data-table >>> .v-data-table-header th {
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Ajustes para lista móvil */
.v-list-item__content {
  padding: 4px 0;
}

/* Eliminar sombras excesivas */
.v-toolbar {
  box-shadow: none !important;
}

/* Inputs más limpios */
.v-text-field--solo >>> .v-input__control {
  min-height: 40px;
}

/* Progress circular más sutil */
.v-progress-circular {
  opacity: 0.8;
}

/* Nueva clase para alineación en vista de facturas */
.invoices-top .v-row {
  align-items: flex-start !important;
}
.invoices-top .v-data-table >>> th,
.invoices-top .v-data-table >>> td {
  vertical-align: top !important;
}
.invoices-top .v-list-item {
  align-items: flex-start !important;
}
</style>
